import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import './App.css';
import { LoginRoute } from './components/Login';
import Sidebar from './components/Sidebar';
import NotificationBell from './components/NotificationBell';
import ProtectedRoute from './components/ProtectedRoute';
import IdleTimeoutWrapper from './components/IdleTimeoutWrapper';
import RoleDashboard from './components/RoleDashboard';
import MasterRoute from './components/MasterRoute';
import TenantRoute from './components/TenantRoute';
import ToastHost from './components/ToastHost';
import { isAuthenticated } from './services/authService';

const AcceptInvite = lazy(() => import('./components/AcceptInvite'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const AgencyPicker = lazy(() => import('./components/AgencyPicker'));
const AgencySuspended = lazy(() => import('./components/AgencySuspended'));
const Documents = lazy(() => import('./components/Documents'));
const DocumentPage = lazy(() => import('./components/DocumentPage'));
const DocumentSignatureSummary = lazy(() => import('./components/DocumentSignatureSummary'));
const AgencyTags = lazy(() => import('./components/AgencyTags'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const BaseFormManagement = lazy(() => import('./components/BaseFormManagement'));
const BaseFormCreate = lazy(() => import('./components/BaseFormCreate'));
const BaseFormPreview = lazy(() => import('./components/BaseFormPreview'));
const Settings = lazy(() => import('./components/Settings'));
const RankStructureManager = lazy(() => import('./components/RankStructureManager'));
const OrgStructureTemplateManager = lazy(() => import('./components/OrgStructureTemplateManager'));
const ApprovalQueue = lazy(() => import('./components/ApprovalQueue'));
const FormSubmissionDetail = lazy(() => import('./components/FormSubmissionDetail'));
const FormSubmissionFlow = lazy(() => import('./components/FormSubmissionFlow'));
const PolicyAcknowledgementList = lazy(() => import('./components/PolicyAcknowledgementList'));
const PolicyDistribution = lazy(() => import('./components/PolicyDistribution'));
const AcknowledgementReport = lazy(() => import('./components/AcknowledgementReport'));
const Organization = lazy(() => import('./components/Organization'));
const EmployeeDirectory = lazy(() => import('./components/EmployeeDirectory'));
const MySubmissions = lazy(() => import('./components/MySubmissions'));
const WorkflowTemplates = lazy(() => import('./components/WorkflowTemplates'));
const WorkflowTemplateBuilder = lazy(() => import('./components/WorkflowTemplateBuilder'));
const DocumentTests = lazy(() => import('./components/DocumentTests'));
const DocumentTestBuilder = lazy(() => import('./components/DocumentTestBuilder'));
const ReviewTask = lazy(() => import('./components/ReviewTask'));
const AgencySettings = lazy(() => import('./components/AgencySettings'));
const AgencyReports = lazy(() => import('./components/AgencyReports'));
const AgencyAdministration = lazy(() => import('./components/AgencyAdministration'));

function Layout() {
  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content" id="main-content">
        <div className="main-top-bar">
          {/* Shown on a phone only (App.css), where the rail that carries the name is a
              closed drawer. Hidden from assistive technology: the drawer still names the
              product, and a second "Veriform" on every page is noise. */}
          <span className="main-top-bar-brand" aria-hidden="true">Veriform</span>
          <NotificationBell />
        </div>
        <Suspense fallback={<div className="route-loading" />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}

/**
 * Chrome-less shell for the pre-context screens (picker, suspended notice) and for a page
 * meant to be printed (a revision's signature summary, DOC-18 #466).
 * Still carries `#main-content` — the skip link targets it on every route, and
 * without it these two screens would offer keyboard users a dead anchor.
 */
function BareLayout() {
  return (
    <main id="main-content">
      <Suspense fallback={<div className="route-loading" />}>
        <Outlet />
      </Suspense>
    </main>
  );
}

function App() {
  return (
    <Router>
      <IdleTimeoutWrapper>
        <div className="App">
          <a href="#main-content" className="skip-link">Skip to main content</a>
          {/* Outside both layouts on purpose: `Layout` unmounts on any navigation
              into `BareLayout` (the picker, the suspended notice) or /login, and a
              toast host that unmounts takes the message with it. Not lazy — it
              must exist before the first route resolves. */}
          <ToastHost />
          <Routes>
            <Route 
              path="/login" 
              element={
                isAuthenticated() ? <Navigate to="/" replace /> : <LoginRoute />
              }
            />
            {/* Invitation acceptance. Top-level and UNGUARDED on purpose: the visitor is an
                invited administrator who has no credential yet — choosing one is the point —
                so every guard here would lock out the only person who may use the page.
                Lazy like every other route component (Login is the one static import,
                because it is the landing page): almost nobody loads this screen, and it has
                no reason to sit in the main bundle. Being top-level it inherits no layout
                Suspense, so it carries its own boundary — without one, React throws on
                first render. */}
            <Route
              path="/accept-invite"
              element={
                <Suspense fallback={<div className="route-loading" />}>
                  <AcceptInvite />
                </Suspense>
              }
            />
            {/* Self-service password reset ("Forgot password?" on the sign-in screen).
                Unguarded for the invitation's reason: the visitor cannot sign in, which
                is the point. Lazy, with its own Suspense boundary, for the same reasons
                as /accept-invite above. It emails a one-time password and then becomes
                the sign-in form; the forced password change that follows is a step of
                Login, not a route — there is no /reset-password page any more, and a link
                from an email sent before 2026-10-09 falls through to the catch-all. */}
            <Route
              path="/forgot-password"
              element={
                <Suspense fallback={<div className="route-loading" />}>
                  <ForgotPassword />
                </Suspense>
              }
            />
            {/* Chrome-less screens: no sidebar, because there is no context to
                navigate yet (picker), none worth offering (suspended), or the
                page is meant to be printed (a revision's signature summary). */}
            <Route
              element={
                <ProtectedRoute>
                  <BareLayout />
                </ProtectedRoute>
              }
            >
              <Route element={<MasterRoute />}>
                <Route path="/select-agency" element={<AgencyPicker />} />
              </Route>
              <Route path="/agency-suspended" element={<AgencySuspended />} />
              {/* DOC-18 (#466): the printable summary of one revision's signatures. Tenant
                  data like the document page, so the same guard; outside the app shell so
                  what prints is the summary, not the sidebar. */}
              <Route element={<TenantRoute />}>
                <Route path="/documents/:id/signature-summary" element={<DocumentSignatureSummary />} />
              </Route>
            </Route>

            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<RoleDashboard />} />

              {/* The registry CRM. Reachable under either claim — its endpoints
                  are @MasterAllowed and the registry entities are qualified. */}
              <Route element={<MasterRoute />}>
                <Route path="/agencies" element={<AdminDashboard />} />
              </Route>

              {/* Screens backed by tenant-schema data: with a master claim every
                  request returns 403 TENANT_NOT_SELECTED, so send the admin to
                  the picker rather than render a screen that cannot load. */}
              <Route element={<TenantRoute />}>
                <Route path="/documents" element={<Documents />} />
                <Route path="/documents/:id" element={<DocumentPage />} />
                <Route path="/workflow-templates" element={<WorkflowTemplates />} />
                <Route path="/workflow-templates/new" element={<WorkflowTemplateBuilder />} />
                <Route path="/workflow-templates/:id" element={<WorkflowTemplateBuilder />} />
                <Route path="/document-tests" element={<DocumentTests />} />
                <Route path="/document-tests/new" element={<DocumentTestBuilder />} />
                <Route path="/document-tests/:id" element={<DocumentTestBuilder />} />
                <Route path="/review-tasks/:taskId" element={<ReviewTask />} />
                <Route path="/form-submissions" element={<ApprovalQueue />} />
                <Route path="/form-submissions/:id" element={<FormSubmissionDetail />} />
                <Route path="/form-submissions/:submissionId/edit" element={<FormSubmissionFlow />} />
                <Route path="/forms/:baseFormId/submit" element={<FormSubmissionFlow />} />
                <Route path="/acknowledgements" element={<PolicyAcknowledgementList />} />
                <Route path="/policy-distribution" element={<PolicyDistribution />} />
                <Route path="/acknowledgement-report" element={<AcknowledgementReport />} />
                <Route path="/my-submissions" element={<MySubmissions />} />
                <Route path="/organization" element={<Organization />} />
                {/* D19: /organization is the one org screen, and since ORG-23 (#318) it
                    shows the people inside their groups. The old chart route redirects
                    rather than 404s, because it is bookmarked and linked from the E2E
                    guide; OrgChart.js and OrgChart.css are deleted, so there is nothing
                    left to route TO. Two org surfaces, not four. */}
                <Route path="/org-chart" element={<Navigate to="/organization" replace />} />
                <Route path="/directory" element={<EmployeeDirectory />} />
                {/* What needs an administrator, over the agency's record. Its own navigation
                    entry; an agency admin's '/' is the ordinary Dashboard. The screen sends
                    anyone below agency administrator home. */}
                <Route path="/administration" element={<AgencyAdministration />} />
                {/* The agency's record (Veriform maintains it), its time zone and escalation
                    interval, group types and temporary-assignment rules. Its own navigation entry
                    for agency administrators and for a platform administrator inside an agency;
                    the screen sends anyone else home. */}
                <Route path="/agency-settings" element={<AgencySettings />} />
                {/* The agency-wide figures. A platform administrator only, for now (owner
                    ruling, 2026-10-28); the screen sends anyone else home. */}
                <Route path="/agency-reports" element={<AgencyReports />} />
              </Route>

              {/* Left unguarded on purpose: these screens are dual-mode and decide
                  for themselves which endpoint to call, so neither guard fits.
                  Guarding them as tenant-only would lock the admin out of the
                  content library they author from master; guarding them with
                  MasterRoute would be worse, because that guard keys on ROLE and
                  would redirect every AGENCY_ADMIN and GROUP_ADMIN away.
                  /users is dual-mode as of P8-09; the catalog screens become so in
                  PR-14B. */}
              <Route path="/users" element={<UserManagement />} />
              <Route path="/tags" element={<AgencyTags />} />
              <Route path="/base-forms" element={<BaseFormManagement />} />
              <Route path="/base-forms/create" element={<BaseFormCreate />} />
              <Route path="/base-forms/edit/:id" element={<BaseFormCreate />} />
              <Route path="/base-forms/preview/:id" element={<BaseFormPreview />} />
              <Route path="/ranks" element={<RankStructureManager />} />
              <Route path="/org-structure-templates" element={<OrgStructureTemplateManager />} />
              <Route path="/settings" element={<Settings />} />
            </Route>
            <Route
              path="*"
              element={<Navigate to={isAuthenticated() ? "/" : "/login"} replace />}
            />
          </Routes>
        </div>
      </IdleTimeoutWrapper>
    </Router>
  );
}

export default App;
